import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR } from '../../../utils/currency';

const AssetDistributionChart = ({ assets = [], categories = [] }) => {
  const [viewType, setViewType] = useState('pie');
  const [groupBy, setGroupBy] = useState('department'); // 'department' | 'category'
  const [selectedItem, setSelectedItem] = useState(null);

  // Map category IDs to names
  const categoriesMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  // Process data for department distribution
  const departmentData = useMemo(() => {
    const deptMap = {};
    const colors = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0EA5E9', '#EC4899', '#14B8A6', '#F59E0B', '#6366F1'];
    
    assets.forEach(asset => {
      const dept = asset.department || 'Unassigned';
      if (!deptMap[dept]) {
        deptMap[dept] = {
          name: dept,
          value: 0,
          assets: 0,
          cost: 0
        };
      }
      deptMap[dept].value++;
      deptMap[dept].assets++;
      deptMap[dept].cost += parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
    });

    return Object.values(deptMap).map((item, index) => ({
      ...item,
      color: colors[index % colors.length]
    })).sort((a, b) => b.value - a.value);
  }, [assets]);

  // Process data for category distribution
  const categoryData = useMemo(() => {
    const catMap = {};
    const colors = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#3B82F6', '#14B8A6'];

    assets.forEach(asset => {
      const catName = categoriesMap[asset.asset_category_id] || asset.category?.name || 'Uncategorized';
      if (!catMap[catName]) {
        catMap[catName] = {
          name: catName,
          value: 0,
          assets: 0,
          cost: 0
        };
      }
      catMap[catName].value++;
      catMap[catName].assets++;
      catMap[catName].cost += parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
    });

    return Object.values(catMap).map((item, index) => ({
      ...item,
      color: colors[index % colors.length]
    })).sort((a, b) => b.value - a.value);
  }, [assets, categoriesMap]);

  // Process data for stacked bar chart
  const stackedData = useMemo(() => {
    const typeMap = {};

    assets.forEach(asset => {
      const categoryName = categoriesMap[asset.asset_category_id] || asset.category?.name || 'Other';
      const dept = asset.department || 'Unassigned';
      
      if (!typeMap[categoryName]) {
        typeMap[categoryName] = { category: categoryName };
      }
      
      if (!typeMap[categoryName][dept]) {
        typeMap[categoryName][dept] = 0;
      }
      typeMap[categoryName][dept]++;
    });

    return Object.values(typeMap);
  }, [assets, categoriesMap]);

  const activeChartData = groupBy === 'department' ? departmentData : categoryData;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-semibold text-popover-foreground">{label || data?.name}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Total Assets: <span className="font-medium text-popover-foreground">{data?.value || data?.assets}</span>
          </p>
          {data?.cost !== undefined && (
            <p className="text-sm text-muted-foreground">
              Total Value: <span className="font-semibold text-success">{formatINR(data?.cost, false)}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const handleItemClick = (name) => {
    setSelectedItem(selectedItem === name ? null : name);
  };

  if (assets.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="text-center py-12">
          <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Asset Distribution Data</h3>
          <p className="text-muted-foreground">Adjust filters or add assets to display distribution analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Asset Distribution</h3>
          <p className="text-sm text-muted-foreground">
            Distribution by {groupBy === 'department' ? 'Department' : 'Asset Category'}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Group Toggle */}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
            <button
              onClick={() => setGroupBy('department')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                groupBy === 'department' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Dept
            </button>
            <button
              onClick={() => setGroupBy('category')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                groupBy === 'category' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Category
            </button>
          </div>

          {/* Chart Type Toggle */}
          <Button
            variant={viewType === 'pie' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('pie')}
            iconName="PieChart"
            iconPosition="left"
          >
            Pie
          </Button>
          <Button
            variant={viewType === 'bar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('bar')}
            iconName="BarChart3"
            iconPosition="left"
          >
            Bar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="lg:col-span-2">
          <div className="h-80">
            {viewType === 'pie' ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={activeChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={105}
                    innerRadius={45}
                    dataKey="value"
                    onClick={(data) => handleItemClick(data?.name)}
                    className="cursor-pointer"
                  >
                    {activeChartData?.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry?.color}
                        stroke={selectedItem === entry?.name ? '#0F172A' : '#ffffff'}
                        strokeWidth={selectedItem === entry?.name ? 3 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stackedData} margin={{ top: 20, right: 30, left: 20, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="category" 
                    tick={{ fontSize: 11 }}
                    stroke="#64748B"
                    angle={-20}
                    textAnchor="end"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#64748B"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {departmentData.map((dept) => (
                    <Bar 
                      key={dept.name}
                      dataKey={dept.name} 
                      stackId="a" 
                      fill={dept.color} 
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Breakdown Details */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-card-foreground">
              {groupBy === 'department' ? 'Department Breakdown' : 'Category Breakdown'}
            </h4>
            <span className="text-xs text-muted-foreground">
              {activeChartData.length} groups
            </span>
          </div>

          <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
            {activeChartData?.map((item) => (
              <div
                key={item?.name}
                className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
                  selectedItem === item?.name
                    ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/40 hover:bg-muted/40'
                }`}
                onClick={() => handleItemClick(item?.name)}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item?.color }}
                    />
                    <span className="text-sm font-medium text-card-foreground truncate max-w-[140px]" title={item?.name}>
                      {item?.name}
                    </span>
                  </div>
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-muted text-foreground">
                    {item?.assets} assets
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground flex justify-between items-center pt-1 border-t border-border/50">
                  <span>Valuation:</span>
                  <span className="font-semibold text-card-foreground">
                    {formatINR(item?.cost, true)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDistributionChart;
